import { AlertCircle, Loader } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';

import { FilterPill } from '../../components/ui';
import { useMailFailuresQuery } from '../../hooks/useHandoff';
import { useHandoffStatsSummaryQuery } from '../../hooks/useHandoffStat';
import type { MailFailure, MailFailureFilter } from '../../types/handoff';

import { MailFailureRow } from './MailFailureRow';

export const MAIL_FAILURES_ORIGIN = { from: 'dashboard' } as const;

type PillKey = 'open' | 'handoff' | 'email_draft' | 'resolved' | 'all';

const PILLS: Array<{ key: PillKey; label: string; query: MailFailureFilter; className: string }> = [
  {
    key: 'open',
    label: 'Öppna',
    query: 'open',
    className: 'bg-slate-100 text-slate-700 border-slate-200',
  },
  {
    key: 'handoff',
    label: 'Handoff-mejl',
    query: 'open',
    className: 'bg-red-100 text-red-800 border-red-200',
  },
  {
    key: 'email_draft',
    label: 'Mejlutkast',
    query: 'open',
    className: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  },
  {
    key: 'resolved',
    label: 'Åtgärdade',
    query: 'resolved',
    className: 'bg-green-50 text-green-800 border-green-200',
  },
  {
    key: 'all',
    label: 'Alla',
    query: 'all',
    className: 'bg-slate-100 text-slate-700 border-slate-200',
  },
];

const matchesPill = (failure: MailFailure, pill: PillKey): boolean =>
  pill === 'handoff' || pill === 'email_draft' ? failure.kind === pill : true;

const mostCommonReason = (failures: MailFailure[]): { reason: string; count: number } | null => {
  const counts = new Map<string, number>();
  for (const failure of failures) {
    if (!failure.reason) continue;
    counts.set(failure.reason, (counts.get(failure.reason) ?? 0) + 1);
  }
  const top = [...counts.entries()].sort((a, b) => b[1] - a[1])[0];
  return top ? { reason: top[0], count: top[1] } : null;
};

export const MailFailuresPage = () => {
  const location = useLocation();
  const cameFromDashboard =
    (location.state as { from?: string } | null)?.from === MAIL_FAILURES_ORIGIN.from;

  if (!cameFromDashboard) {
    return <Navigate to="/dashboard" replace />;
  }

  return <MailFailuresView />;
};

const MailFailuresView = () => {
  const [pill, setPill] = useState<PillKey>('open');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const activePill = PILLS.find((p) => p.key === pill) ?? PILLS[0];
  const { data: failures = [], isLoading, error } = useMailFailuresQuery(activePill.query);
  const { data: summary } = useHandoffStatsSummaryQuery();

  const visible = useMemo(
    () => failures.filter((failure) => matchesPill(failure, pill)),
    [failures, pill],
  );
  const openFailures = useMemo(() => failures.filter((f) => !f.isResolved), [failures]);
  const topReason = useMemo(() => mostCommonReason(openFailures), [openFailures]);

  return (
    <section className="space-y-6">
      <div>
        <Link to="/dashboard" className="text-sm text-slate-500 transition hover:text-brand-blue">
          ← Översikt
        </Link>
        <h2 className="mt-2 font-display text-3xl font-semibold text-brand-blue">
          Mejl som aldrig nådde kundservice
        </h2>
        <p className="mt-1 max-w-3xl text-sm text-text-grey">
          Kunden har fått löfte om svar, men mejlet till kundservice gick inte fram — antingen ett
          handoff-mejl eller ett AI-skrivet mejlutkast. Ärendena ligger kvar tills någon markerat
          dem som åtgärdade.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-xl border border-red-300 bg-red-50 p-5">
          <p className="text-xs font-semibold uppercase tracking-wider text-red-700">
            Öppna ärenden
          </p>
          <p className="mt-3 text-3xl font-bold text-red-700">{openFailures.length}</p>
          <p className="mt-3 border-t border-red-200 pt-3 text-xs text-red-700">
            Kunder som väntar på svar som ingen sett.
          </p>
        </div>
        <div className="rounded-xl border border-brand-blue/15 bg-white p-5">
          <p className="text-xs font-semibold uppercase tracking-wider text-brand-blue/70">
            Senaste 7 dagarna
          </p>
          <p className="mt-3 text-3xl font-bold text-brand-blue">
            {summary?.mailFailures?.last7Days ?? 0}
          </p>
          <p className="mt-3 border-t border-brand-blue/10 pt-3 text-xs text-brand-blue/60">
            Nya fel den senaste veckan, oavsett om de åtgärdats.
          </p>
        </div>
        <div className="rounded-xl border border-brand-blue/15 bg-white p-5">
          <p className="text-xs font-semibold uppercase tracking-wider text-brand-blue/70">
            Vanligaste orsak
          </p>
          <p className="mt-3 text-base font-semibold leading-snug text-brand-blue">
            {topReason ? topReason.reason : 'Inga öppna fel'}
          </p>
          <p className="mt-3 border-t border-brand-blue/10 pt-3 text-xs text-brand-blue/60">
            {topReason
              ? `${topReason.count} av ${openFailures.length} öppna ärenden.`
              : 'Alla eskaleringsmejl har gått fram.'}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="text-slate-500">Visa:</span>
        {PILLS.map((option) => (
          <FilterPill
            key={option.key}
            active={pill === option.key}
            tone={option.className}
            onClick={() => {
              setPill(option.key);
              setExpandedId(null);
            }}
          >
            {option.label}
          </FilterPill>
        ))}
        <span className="text-slate-400">
          ({visible.length} av {failures.length})
        </span>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader className="h-6 w-6 animate-spin text-brand-blue" />
        </div>
      ) : error ? (
        <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          <AlertCircle className="h-5 w-5 flex-shrink-0" />
          <span>Kunde inte hämta listan: {(error as Error).message}</span>
        </div>
      ) : visible.length === 0 ? (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-8 text-center text-slate-600">
          {pill === 'resolved'
            ? 'Inga åtgärdade ärenden än.'
            : 'Inga mejl har fastnat — allt har nått kundservice.'}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-brand-beige-tint-1">
              <tr className="text-left text-xs uppercase tracking-wide text-slate-600">
                <th className="w-8 px-3 py-2" />
                <th className="px-3 py-2">Tid</th>
                <th className="px-3 py-2">Kund</th>
                <th className="px-3 py-2">Kanal</th>
                <th className="px-3 py-2">Typ</th>
                <th className="px-3 py-2">Orsak</th>
                <th className="px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {visible.map((failure) => (
                <MailFailureRow
                  key={failure.sessionId}
                  failure={failure}
                  expanded={expandedId === failure.sessionId}
                  onToggle={() =>
                    setExpandedId(expandedId === failure.sessionId ? null : failure.sessionId)
                  }
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};
